import { useCallback, useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Wallet, Plus, Trash2, Upload, ArrowUpRight, ArrowDownLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ['Salaire', 'Loyer', 'Courses', 'Transport', 'Loisirs', 'Santé', 'Autre'];

export const FinancesPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState({ balance: 0, total_income: 0, total_expenses: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type: 'expense',
    amount: '',
    category: 'Autre',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const { token } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [transRes, balanceRes] = await Promise.all([
        axios.get(`${API}/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/transactions/balance`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTransactions(transRes.data);
      setBalance(balanceRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addTransaction = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) return;
    
    setAdding(true);
    try {
      await axios.post(`${API}/transactions`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setFormData({
        type: 'expense',
        amount: '',
        category: 'Autre',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      setShowForm(false);
      fetchData();
    } catch (error) {
      console.error('Error adding transaction:', error);
    } finally {
      setAdding(false);
    }
  };

  const deleteTransaction = async (id) => {
    try {
      await axios.delete(`${API}/transactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/transactions/import-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert(`${response.data.imported} transactions importées !`);
      fetchData();
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert('Erreur lors de l\'import. Vérifie le format du fichier.');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A2E] px-4 py-6" data-testid="finances-page">
      <div className="max-w-4xl mx-auto">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#7EC8C8] mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          <span>Retour au dashboard</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#7EC8C8]/30 to-[#FF6B6B]/30 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-[#F8F9FA]" />
            </div>
            <h1 
              className="text-3xl font-bold text-[#F8F9FA]"
              style={{ fontFamily: 'Outfit' }}
            >
              Mes Finances
            </h1>
          </div>
          <p className="text-[#94A3B8] mb-8">
            Gère tes entrées et sorties d'argent pour garder le contrôle.
          </p>
        </motion.div>

        {/* Balance cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
        >
          <Card className="bg-gradient-to-br from-[#7EC8C8]/20 to-[#4ECDC4]/10 border-[#7EC8C8]/30 p-6 text-center">
            <p className="text-sm text-[#94A3B8] mb-1">Solde actuel</p>
            <p className={`text-3xl font-bold ${balance.balance >= 0 ? 'text-[#4ECDC4]' : 'text-[#FF6B6B]'}`}>
              {balance.balance.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
            </p>
          </Card>
          <Card className="bg-[#4ECDC4]/10 border-[#4ECDC4]/20 p-6 text-center">
            <p className="text-sm text-[#94A3B8] mb-1">Total entrées</p>
            <p className="text-2xl font-bold text-[#4ECDC4]">
              +{balance.total_income.toLocaleString('fr-FR')}€
            </p>
          </Card>
          <Card className="bg-[#FF6B6B]/10 border-[#FF6B6B]/20 p-6 text-center">
            <p className="text-sm text-[#94A3B8] mb-1">Total sorties</p>
            <p className="text-2xl font-bold text-[#FF6B6B]">
              -{balance.total_expenses.toLocaleString('fr-FR')}€
            </p>
          </Card>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap gap-3 mb-8"
        >
          <Button
            onClick={() => setShowForm(!showForm)}
            data-testid="add-transaction-btn"
            className="bg-[#7EC8C8] hover:bg-[#4ECDC4] text-[#1A1A2E]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une transaction
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            variant="outline"
            data-testid="import-csv-btn"
            className="border-[#7EC8C8]/50 text-[#7EC8C8] hover:bg-[#7EC8C8]/10"
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importer CSV
          </Button>
        </motion.div>

        {/* Add form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card className="bg-[#1A1A2E]/50 border-[#7EC8C8]/20 p-6 mb-8">
              <form onSubmit={addTransaction} className="space-y-4">
                <div className="flex gap-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, type: 'income' })}
                    className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                      formData.type === 'income' 
                        ? 'bg-[#4ECDC4] text-[#1A1A2E]' 
                        : 'bg-[#4ECDC4]/20 text-[#4ECDC4]'
                    }`}
                  >
                    <ArrowDownLeft className="w-4 h-4 inline mr-2" />
                    Entrée
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, type: 'expense' })}
                    className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                      formData.type === 'expense' 
                        ? 'bg-[#FF6B6B] text-white' 
                        : 'bg-[#FF6B6B]/20 text-[#FF6B6B]'
                    }`}
                  >
                    <ArrowUpRight className="w-4 h-4 inline mr-2" />
                    Sortie
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Montant (€)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="amount-input"
                      className="bg-[#F8F9FA]/5 border-[#7EC8C8]/20 text-[#F8F9FA]"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Catégorie</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full h-10 px-3 rounded-md bg-[#F8F9FA]/5 border border-[#7EC8C8]/20 text-[#F8F9FA]"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat} className="bg-[#1A1A2E]">{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Description</label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Ex: Courses Carrefour"
                      className="bg-[#F8F9FA]/5 border-[#7EC8C8]/20 text-[#F8F9FA]"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Date</label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="bg-[#F8F9FA]/5 border-[#7EC8C8]/20 text-[#F8F9FA]"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={adding}
                    className={formData.type === 'income' 
                      ? 'bg-[#4ECDC4] hover:bg-[#7EC8C8] text-[#1A1A2E]'
                      : 'bg-[#FF6B6B] hover:bg-[#ff5252] text-white'
                    }
                  >
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowForm(false)}
                    className="text-[#94A3B8]"
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        )}

        {/* Transactions list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-lg font-semibold text-[#F8F9FA] mb-4" style={{ fontFamily: 'Outfit' }}>
            Historique ({transactions.length})
          </h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#7EC8C8]" />
            </div>
          ) : transactions.length === 0 ? (
            <Card className="bg-[#1A1A2E]/50 border-[#7EC8C8]/20 border-dashed p-12 text-center">
              <p className="text-[#94A3B8]">Aucune transaction. Commence par en ajouter une !</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {transactions.map((transaction) => (
                <Card 
                  key={transaction.id}
                  className="bg-[#1A1A2E]/50 border-[#7EC8C8]/20 p-4 hover:border-[#7EC8C8]/40 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      transaction.type === 'income' 
                        ? 'bg-[#4ECDC4]/20' 
                        : 'bg-[#FF6B6B]/20'
                    }`}>
                      {transaction.type === 'income' 
                        ? <ArrowDownLeft className="w-5 h-5 text-[#4ECDC4]" />
                        : <ArrowUpRight className="w-5 h-5 text-[#FF6B6B]" />
                      }
                    </div>
                    <div className="flex-1">
                      <p className="text-[#F8F9FA] font-medium">
                        {transaction.description || transaction.category}
                      </p>
                      <p className="text-xs text-[#94A3B8]">
                        {transaction.category} • {transaction.date}
                      </p>
                    </div>
                    <p className={`text-lg font-semibold ${
                      transaction.type === 'income' ? 'text-[#4ECDC4]' : 'text-[#FF6B6B]'
                    }`}>
                      {transaction.type === 'income' ? '+' : '-'}
                      {transaction.amount.toLocaleString('fr-FR')}€
                    </p>
                    <button
                      onClick={() => deleteTransaction(transaction.id)}
                      className="p-2 text-[#94A3B8] hover:text-[#FF6B6B] transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default FinancesPage;
